"use client"

import { useState, useRef, useEffect } from "react"
import { X, ChevronDown, Check } from "lucide-react"
import { cn } from "@/lib/utils"

interface Option {
  value: string
  label: string
}

interface MultiSelectComboboxProps {
  options: Option[]
  value: string[]
  onChange: (value: string[]) => void
  placeholder?: string
  className?: string
  required?: boolean
}

export function MultiSelectCombobox({
  options,
  value,
  onChange,
  placeholder = "Seçiniz...",
  className,
  required = false,
}: MultiSelectComboboxProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
        setSearchTerm("")
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const selectedLabels = options
    .filter((option) => value.includes(option.value))
    .map((option) => option.label)

  const toggleOption = (optionValue: string) => {
    if (value.includes(optionValue)) {
      onChange(value.filter((v) => v !== optionValue))
    } else {
      onChange([...value, optionValue])
    }
  }

  const removeOption = (optionValue: string, e: React.MouseEvent) => {
    e.stopPropagation()
    onChange(value.filter((v) => v !== optionValue))
  }

  return (
    <div ref={containerRef} className={cn("relative w-full", className)}>
      <div
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "flex min-h-[3rem] w-full cursor-pointer items-center gap-2 rounded-md border border-slate-300 bg-background px-3 py-2 text-sm ring-offset-background transition-colors",
          "focus-within:outline-none focus-within:ring-2 focus-within:ring-[rgb(3,74,166)] focus-within:ring-offset-2",
          isOpen && "ring-2 ring-[rgb(3,74,166)] ring-offset-2"
        )}
      >
        <div className="flex flex-1 flex-wrap gap-1.5">
          {selectedLabels.length > 0 ? (
            selectedLabels.map((label, index) => {
              const optionValue = options.find((opt) => opt.label === label)?.value
              return (
                <span
                  key={optionValue}
                  className="inline-flex items-center gap-1 rounded-md bg-[rgb(3,74,166)]/10 px-2 py-1 text-xs font-medium text-[rgb(3,74,166)]"
                >
                  {label}
                  <button
                    type="button"
                    onClick={(e) => optionValue && removeOption(optionValue, e)}
                    className="rounded-full hover:bg-[rgb(3,74,166)]/20 p-0.5 transition-colors"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              )
            })
          ) : (
            <span className="text-slate-500">{placeholder}</span>
          )}
        </div>
        <ChevronDown
          className={cn(
            "h-4 w-4 shrink-0 text-slate-400 transition-transform",
            isOpen && "rotate-180"
          )}
        />
      </div>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-slate-200 bg-white shadow-lg">
          <div className="p-2">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Ara..."
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[rgb(3,74,166)]"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <div className="max-h-60 overflow-auto p-1">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => {
                const isSelected = value.includes(option.value)
                return (
                  <div
                    key={option.value}
                    onClick={() => toggleOption(option.value)}
                    className={cn(
                      "flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors",
                      "hover:bg-slate-100",
                      isSelected && "bg-[rgb(3,74,166)]/10 text-[rgb(3,74,166)]"
                    )}
                  >
                    <div
                      className={cn(
                        "flex h-4 w-4 items-center justify-center rounded border-2",
                        isSelected
                          ? "border-[rgb(3,74,166)] bg-[rgb(3,74,166)]"
                          : "border-slate-300"
                      )}
                    >
                      {isSelected && <Check className="h-3 w-3 text-white" />}
                    </div>
                    <span>{option.label}</span>
                  </div>
                )
              })
            ) : (
              <div className="px-3 py-2 text-sm text-slate-500">Sonuç bulunamadı</div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

